import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { HubConnectionState } from '@microsoft/signalr';
import { Action } from '@ngrx/store';
import { IDialogConfig, NotificationType } from '@ra-web-tech-ui-toolkit/components';
import { AppId, UserProfile } from '@rockwell-automation-inc/common-utils';
import { CoreConfiguration } from '@rockwell-automation-inc/service';
import { DomainEventName } from '../services/command-request.service';

export interface Configuration extends CoreConfiguration {
  adminPortalURL: string;
}

export interface AppState {
  controlPage: PageControl;
  signalR: SignalRData;
  snackBarData: SnackBarMessage;
  user: UserState;
  trials: TrialsState;
  reports: ReportState;
  tenant: TenantState;
  sessionManagement: SessionManagement;
  featureFlags: FeatureFlags;
}

export interface FeatureFlags {
  flags?: Record<string, FlagValue>;
}

export interface ReportState {
  previousReportData?: ReportData;
}

export interface PageControl {
  inflightRequests: number;
  loggedInUserProfile?: UserProfile;
  catalogInfo?: ICatalogInfo[];
}

export interface IEntitlemetState {
  entitlements: any[];
  total?: number;
  currentPage?: number;
}

export type SortModel = {
  colId: string;
  sort: 'asc' | 'desc';
};

export type FilterType =
  | 'greaterThanOrEqual'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'lessThan'
  | 'equals'
  | 'notEqual'
  | 'inRange'
  | 'contains';

export type FilterContext = {
  filterType: 'set' | 'number' | 'text' | 'date';
  type?: FilterType;
  filter?: any;
  dateTo?: Date;
  dateFrom?: Date;
  values?: string[];
  filterTo?: any;
};

export type FilterModel = {
  [key: string]: FilterContext;
};
export interface SortFilterContext {
  sortModel: SortModel[];
  filterModel: FilterModel;
}

export interface EntitlementContext {
  perPage: number;
  pageNum: number;
  contractNo?: string;
  email?: string;
  sortFilterOpts?: SortFilterContext;
  tenantId?: string;
}

export interface ReportContext {
  reportType: 'entitlement' | 'trial reservation';
  reportUiUrl: string;
  requestEndUrl: string;
  context: {
    [key: string]: string;
  };
}

export interface TrialsReservations {
  perPage: number;
  pageNum: number;
  sortFilterOpts?: SortFilterContext;
}

export interface PaginatedResponse<T> {
  currentPage: number;
  records: T[];
  total: number;
}

export type AgFilterModel = {
  [key: string]: AgFilterContext;
};
export type AgFilterContext = {
  filterType: 'set' | 'number' | 'text' | 'date';
  type?: FilterType;
  filter?: any;
  filterTo?: any;
  dateTo?: string;
  dateFrom?: string;
  values?: (string | null)[];
};

export interface ICatalogInfo {
  catalogCode: string;
  name: string;
  description: string;
  attributes: any[];
  serviceKind: string;
  productNumber?: string;
}

export enum EntitlementBadge {
  addon = 'Add-On',
  platform = 'Platform',
  additive = 'Additive',
  trial = 'Trial',
  utilityToken = 'Universal Credits',
}

export enum EntitylementTypes {
  addOns = 'addon',
  trial = 'trial',
  platform = 'platform',
  additive = 'additive',
  utilityToken = 'utilityToken',
}

export enum PreviewAppId {
  Fiix = 'Fiix',
  EPlan = 'EPlan',
  Plex = 'Plex',
  CDS = 'CDS',
  FactoryTalkMotionAnalyzer = 'FactoryTalkMotionAnalyzer',
  Advisor = 'Advisor',
}

export type UniversalCredits = 'UniversalCredits';

export function isPreviewApp(s: AppId | PreviewAppId | string): s is PreviewAppId {
  return s in PreviewAppId;
}

export type ApplicationCard = {
  appName: string;
  appId: AppId;
  isDefaultTrialEnabled?: boolean;
};

export interface TenantService extends Service {
  canAccess: boolean;
  role: string;
  provisioned: boolean;
}

export enum MessageIcons {
  Info = 'info-blue.svg',
  Success = 'check-circle-green.svg',
  Error = 'client-error-red.svg',
  Warning = 'warning-yellow.svg',
  LicenseScroll = 'license-scroll.svg',
}

export enum Role {
  Navigator = 'Navigator',
  Reader = 'Reader',
  Contributor = 'Contributor',
  Admin = 'Admin',
  Owner = 'Owner',
  BillingAdmin = 'BillingAdmin',
}

export interface InviteAdmin {
  payloadBody: {
    userEmail: string;
    tenantId: string;
    contextData: any;
  };
  message: string;
}

export interface ErrorContext {
  url: string;
  message: string;
  correlationId?: string;
}
export interface ErrorMessageMap {
  [httpStatus: number]: string;
}
export interface IContext {
  correlationId: string;
  causationId: string;
  userId: string;
  tenantId: string;
}

export type AnyObject = Record<string, unknown>;

export interface IDomainMessage {
  type: DomainEventName | string; // NOSONAR
  data: AnyObject;
  context: IContext;
}

export function isDomainMessage(val: unknown): val is IDomainMessage {
  // eslint-disable-next-line sonarjs/different-types-comparison
  return (val as IDomainMessage)?.context !== undefined;
}

export interface IDomainUpdateResponse {
  response?: HttpResponse<object>;
  message?: IDomainMessage;
  error?: HttpErrorResponse;
  errorContext?: any;
}
export interface SignalRData {
  connectionState?: HubConnectionState;
  domainMessage?: IDomainMessage;
  lastError?: any;
}

export interface SessionManagement {
  isUserActive?: boolean;
  userActivityRecorded: boolean;
  sessionTimedOut: boolean;
  extendSession: boolean;
  logoutSession: boolean;
  lastUserActivityRecordedAt: number;
}
export enum LoginErrorCode {
  AdminPortalRoleNotFound = 'adminPortalRoleNotFound',
  MissingIdentity = 'MissingIdentity',
  MissingConnectionClaim = 'MissingConnectionClaim',
  MissingUniqueIdentifierClaim = 'MissingUniqueIdentifierClaim',
  RootClaimMissing = 'RootClaimMissing',
  InvalidConnectionClaim = 'InvalidConnectionClaim',
  InvalidMergeCandidates = 'InvalidMergeCandidates',
  PreApprovalCheckFailed = 'PreApprovalCheckFailed',
  Unknown = 'Unknown',
}
export interface LoginError {
  errorCode: LoginErrorCode;
  errorDescription: string;
  email: string;
}
export enum ResourceType {
  Tenant = 'Tenant',
  Service = 'Service',
}

export type ResourceTypeString = keyof typeof ResourceType;
export interface Resource {
  resourceId: string;
  resourceName: string;
  resourceType: ResourceTypeString;
}

export type Invitation = Resource & {
  id: string;
  toEmail: string;
  acceptedByUser: string;
  acceptedByUserName: string;
  createdBy: string;
  createdByUserName: string;
  sentDate: Date;
  tenantId: string;
  role: Role;
  status: string;
  expiry: Date;
  buttonsConfig?: any;
};

export interface SnackBarData {
  message: string;
  type: string;
  title?: string;
  duration?: number;
  position?: string;
  customBtn?: CustomBtn[];
}

export interface CustomBtn {
  label: string;
  navigateTo: string;
}

export interface SnackBarMessage {
  data?: SnackBarData;
}

export interface ErrorContext {
  url: string;
  message: string;
  correlationId?: string;
}

export interface Version {
  apiVersion: string;
  portalVersion: string;
  notificationsVersion: string;
}

export interface ActiveCampaign {
  id: string;
  partitionId: string;
  viewType: string;
  status: string;
  reatedOn: string;
  name: string;
  trialDurationInDays: number;
  trialRedemptionLimitInDays: number;
  createdByUserId: string;
  createdByUser: string;
  endedByUserId: string;
  endedByUser: string;
  remainingCredits: number;
  perTrialCredits: number;
  campaignCredits: number;
}

export interface CampaignDetails {
  bundleId: string;
  campaignType: CampaignType;
  createdByUser: string;
  createdByUserId: string;
  createdOn: Date | string;
  description: string;
  endedByUser: string;
  endedByUserId: string;
  endedOn: Date | string;
  id: string;
  name: string;
  partitionId: string;
  pausedByUser: string;
  pausedByUserId: string;
  pausedOn: Date | string;
  redeemedTrials: number;
  reservedTrials: number;
  status: TrialCampaignStatus;
  trialDurationInDays: string;
  trialRedemptionLimitInDays: number;
  viewType: string;
  perTrialCredits: number;
  credits: number;
  trialsQuantity: number;
  remainingTrialsQuantity: number;
  availableReservations?: string;
}

export interface ReserveTrial {
  campaignId: string;
  customerInfo: {
    name: string;
    email: string;
    company: string;
    count: number;
  };
}

export interface CancelTrial {
  campaignId: string;
  reservationId: string;
}

export interface TrialsState {
  campaignDetails: CampaignDetails;
  trialBundles: TrialBundle[];
  refreshGrid: boolean;
}
export type StreamState = { index: number; dead: boolean };
export type StreamStatus = Record<string, StreamState>;
export enum StreamHandler {
  Projector = 'core.Projector.Handler',
  Reactor = 'core.Reactor.Handler',
  Forwarder = 'core.Reactor.ForwarderHandler',
}
export const StreamStateZERO: StreamState = { index: 0, dead: false };
export interface IStreamStatus {
  get isHealthy(): boolean;
  get isUnhealthy(): boolean;
  getHandlerState(handler: StreamHandler): StreamState;
  get tooltip(): string;
}
export interface UserState {
  userId: string;
  name: string;
  location: string;
  email: string;
  company: string;
  secondaryEmails: string[];
  idpConnectionName: string;
  organizations: Organizations[];
  refreshGrid: boolean;
  additionalProfileEmails: string[];
}

export interface TenantState {
  tenantId: string;
  tenantUtilityTokens?: TenantUtilityTokens;
  tenantUsers: TenantUser[];
  selectedTenantUsers: PaginatedResponse<TenantUser>;
  tenantInfo: ITenantDetail;
  refreshGrid: boolean;
  tenantEffectiveRoles?: EffectiveRole[];
}

export interface Organizations {
  name: string;
  id: string;
}

export interface ServiceResolutionData {
  resolutionData: {
    error: string;
  };
  resolutionType: {
    Case: ResolutionType;
  };
}

export type ResolutionType = 'Completed' | 'Failed' | 'Cancelled';

export class ServiceResolutionError extends Error {
  constructor(public errorCode: string) {
    super();
    this.name = 'ServiceResolutionError';
  }
}

export enum RASymbols {
  MINUS = '—',
}

export const TrialReservationStatus = {
  active: 'Active',
  ended: 'Ended',
  cancelled: 'Cancelled',
  linkexpired: 'Link Expired',
  expired: 'Trial Expired',
  redeemed: 'Redeemed',
  unredeemed: 'Unredeemed',
  provisioning: 'Provisioning',
} as const;

export enum TrialCampaignStatus {
  active = 'Active',
  ended = 'Ended',
  paused = 'Paused',
}

export interface ChipInfo {
  color: any;
  text: string;
}

export interface Attributes {
  combineType: string;
}
export interface Entitlement {
  id: string;
  catalogNumber: string;
  contractNumber: string;
  status: string;
  name: string;
  description: string;
  serviceKind: AppId | '' | UniversalCredits;
  effectiveDate: Date;
  expiryDate: Date;
  primaryEmail: string;
  consumed?: number;
  userId: string;
  tenantId: string;
  serviceId: string;
  fromHybris: boolean;
  quantity: number;
  isSystemGenerated: boolean;
  attributes: Attributes;
  available: number;
  timestamp: string;
  allocated: number;
}
export interface TokenEntitlement extends Entitlement {
  entitlementId: string;
}

export interface AGGridEntitlement extends Entitlement {
  entitlementHeaders: AgGridEntitlementColumnsFormat;
  productFamily: string;
  entitlementType: string;
}
export interface EntitlementData {
  entitlement: Entitlement;
  app: ApplicationCard;
  serviceId?: string;
  tenantId: string;
  userId: string;
  continue?: boolean;
}
export interface TenantUtilityTokens {
  id: string;
  tokenBalance: number;
  tokenExpiration?: Date;
  tokenEntitlements: Entitlement[];
  disabledTokenEntitlements: Entitlement[];
}

export interface TenantUtilityTokensResponse {
  id: string;
  tokenBalance: number;
  tokenExpiration?: Date;
  tokenEntitlements: TokenEntitlement[];
  disabledTokenEntitlements: TokenEntitlement[];
}
export interface AgGridEntitlementColumnsFormat {
  header: string;
  subheader: string;
}

export interface OwnerInfo {
  id: string;
  email: string;
  name: string;
}
export interface Service {
  serviceId: string;
  kind: AppId;
  ownedBy: OwnerInfo;
  entitlements: Entitlement[];
}

export interface AgGridService {
  serviceId: string;
  serviceName: string;
  ownerName: string;
  ownerEmail: string;
  userId: string;
  status: string;
}

export interface DisabledService extends Service {
  disabledReason: string;
}

export interface PendingService {
  serviceId: string;
  kind: AppId;
  entitlement: Entitlement | null;
}

export type EntitlmentService = DisabledService | Service;

export interface ITenantDetail {
  id: string;
  name: string;
  ownerName: string;
  owner: string;
  location: string;
  emailDomain: string;
  visibility: string;
  description: string;
  services: Service[];
  disabledServices: DisabledService[];
  pendingServices: PendingService[];
  entitlements: Entitlement[];
  tenantStatus: TenantStatus;
  effectiveTenantStatus: TenantStatus;
  parent?: string;
}

export interface TransferOwnership {
  requestBody: {
    assignee: string;
    resources: string[];
  };
  tenantId: string;
  message: string;
}

export interface ConfirmModalConfig {
  config: IDialogConfig;
  type: NotificationType;
  action: Action;
}

export enum EntitlementCombineType {
  Platform = 'platform',
  Additive = 'additive',
  Addon = 'addon',
  Credits = 'credit',
}
export enum EntitlementDurationType {
  Perpetual = 'perpetual',
  Timebound = 'timebound',
}
export enum EntitlementStatus {
  ACTIVE = 'Active',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
}

export enum AllocatedEntitlementStatus {
  ACTIVE = 'Active',
  PENDING = 'Pending',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
}

export enum ServiceStatus {
  ACTIVE = 'Active',
  DISABLED = 'Disabled',
  PENDING = 'Pending',
}

export type TenantStatus = 'Active' | 'Archived';

export type TenantId = {
  tenantId: string;
};

export type Pagination = {
  perPage: number;
  pageNum: number;
};

export type PaginatedRequest = {
  tenantId: string;
  sortFilterContext?: SortFilterContext;
} & Partial<Pagination>;

export interface TenantHierarchyUsersRequest extends Required<PaginatedRequest> {
  includeTree: boolean;
  includeSiblings: boolean;
}

export interface PageResponse<T> {
  currentPage: number;
  records: T[];
  total: number;
}

export interface TenantListPage<T> {
  tenants: PageResponse<T>;
  archivedCount: number;
}

export interface TenantUser {
  firstName: string;
  lastName: string;
  userEmail: string;
  userId: string;
  userName: string;
  hasAccessToTenant?: boolean;
}

export interface TenantUserAccessService {
  id: string;
  kind: string;
  enabled: boolean;
  effectiveRole: string;
  directRole: string | null;
}

export interface RoleNode {
  id: string;
  effectiveRole: string;
  directRole: string | null;
  name: string;
  fullName: string;
  parent: string | null;
  services: TenantUserAccessService[];
  children: RoleNode[];
}

export interface TenantUserAccessRecord {
  user: TenantUser;
  access: RoleNode;
}

export type CreateCampaign = {
  name: string;
  description: string;
  trialsQuantity: number;
  trialDurationInDays: number;
  trialRedemptionLimitInDays: number;
  perTrialCredits: number;
  bundleId: string;
  campaignType: CampaignType;
};

export enum AppBannerId {
  SessionTimeout = 'SessionTimeout',
}

export type TenantUserService = {
  ownerEmail: string;
  ownerName: string;
  serviceId: string;
  serviceName: string;
  userId: string;
};

export interface FeatureFlagUpdate {
  flag: FeatureFlag;
  value: FlagValue;
}

export type HarnessIdentifier = UserId | 'common-admin-portal';

export interface FeatureFlagTarget {
  identifier: HarnessIdentifier;
  attributes: {
    // choice type for target
    Type: 'User' | 'Application';
    Domain?: string;
  };
}

export type UserId = string & { isGuid: true };

export type FlagValue = boolean | string | number | object | undefined;

export type FeatureFlag = 'enable_language_switcher' | 'enable_user_assigned_trials';

export interface FeatureFlagUpdate {
  flag: FeatureFlag;
  value: FlagValue;
}

export interface FeatureFlagTarget {
  identifier: HarnessIdentifier;
  attributes: {
    // choice type for target
    Type: 'User' | 'Application';
    Domain?: string;
  };
}

export interface TrialBundle {
  id: string;
  //This has been added for UI filtering on name and description
  nameAndDescription: [string, string];
  //This has been added for UI filtering on credits and product catalogs
  creditsAndProductCatalogs: string[];
  name: string;
  description: string;
  isActive: boolean;
  credits: string | null;
  products: (
    | {
        catalog: string;
        quantity: number;
      }
    | string
  )[];
}

export type CampaignNameAndDescription = {
  name: string;
  description: string;
};

export type CampaignUpdateRequest = {
  id: string;
  update: CampaignNameAndDescription;
};

export enum HasCredits {
  'with-credits' = 'With credits',
  'without-credits' = 'Without credits',
}

export interface RemoveUserRequest extends TenantId {
  userId: string;
}

export type ReportData = {
  downloadUrl: string;
  filename: string;
  kind: string;
  openUrl: string;
  status: string;
};

export interface IButtonState {
  disabled: boolean;
  disabledReason: string;
}

export type TenantRole = {
  role: Role;
  tenantId: string;
};
export type EffectiveRole = Resource & TenantRole;

export type TenantUserRoleGridData = {
  resourceType: string;
  resourceName: string;
  role: string;
};

export interface SendRedemptionLinkPayload {
  campaignId: string;
  reservationIds: string[];
}

export type CampaignType = 'user' | 'organization';
